import React from 'react';
import Avatar from '../../../core/design-system/components/Avatar';
import { useProfileNavigation } from '../../../features/profile/hooks/useProfileNavigation';
import './styles.css';

/**
 * Composant qui encapsule l'Avatar et ajoute la fonctionnalité de navigation vers le profil
 * @param {Object} props - Les propriétés du composant
 * @param {Object} props.user - L'utilisateur dont l'avatar est affiché
 * @param {string} props.size - La taille de l'avatar (xs, sm, md, lg, xl)
 * @param {string} props.className - Classes CSS additionnelles
 * @param {boolean} props.disabled - Si true, désactive la navigation même si elle est possible
 * @returns {JSX.Element} - Le composant ClickableAvatar
 */
const ClickableAvatar = ({ 
  user, 
  size = 'md', 
  className = '',
  disabled = false,
  ...otherProps 
}) => {
  const { canNavigate, handleNavigate, navigationTitle } = useProfileNavigation(user);
  
  // Fonction pour gérer le clic sur l'avatar
  const handleClick = (e) => {
    e.stopPropagation(); // Éviter la propagation du clic
    if (canNavigate && !disabled) {
      handleNavigate();
    }
  };

  // Préparation du texte pour l'avatar (première lettre du nom)
  // Utiliser displayName ou name, avec un fallback plus significatif
  const userName = user?.displayName || user?.name || '';
  const avatarText = userName.charAt(0)?.toUpperCase() || 'U';
  
  // Classes CSS conditionnelles
  const clickableClass = canNavigate && !disabled ? 'clickable-avatar' : '';
  const containerClasses = `avatar-container ${clickableClass} ${className}`;

  return (
    <div 
      className={containerClasses}
      onClick={handleClick}
      title={navigationTitle}
      role={canNavigate && !disabled ? "button" : undefined}
      tabIndex={canNavigate && !disabled ? 0 : undefined}
      aria-label={navigationTitle}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && canNavigate && !disabled) {
          e.preventDefault();
          handleNavigate();
        }
      }}
    >
      <Avatar 
        text={avatarText}
        src={user?.photoURL}
        size={size}
        {...otherProps}
      />
    </div>
  );
};

export default ClickableAvatar; 